'use strict';

define('vb/errors/httpError',[], () => {
  const ERROR_CODE = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
  };

  const STATUS_CODE_PROPERTY = 'statusCode';

  /**
   * Represents an HTTP error with a specific status code.
   * Extends the built-in `Error` class to provide additional functionality.
   * @category Helper
   */
  class HttpError extends Error {
    /**
     * Creates a new instance of the HttpError class.
     *
     * @param {number} statusCode - The HTTP status code associated with the error.
     * @param {Error|Object} cause - The underlying cause of the error. Can be an Error object or any other object.
     * @param {String} [message=cause.message] - A human-readable description of the error.
     * Defaults to the message of the cause if provided.
     * @param {...any} params - Additional parameters to pass to the parent Error constructor.
     */
    constructor(statusCode, cause, message = cause.message, ...params) {
      super(message, ...params);
      // Make statusCode and cause 2 read-only properties
      Object.defineProperties(this, {
        [STATUS_CODE_PROPERTY]: {
          get: () => statusCode,
          enumerable: true,
        },
        cause: {
          get: () => cause,
          enumerable: true,
        },
      });

      // Maintains proper stack trace for where our error was thrown (only available on V8)
      if (Error.captureStackTrace) {
        Error.captureStackTrace(this, HttpError);
      }
    }

    /**
     * Checks if the HTTP error status code indicates an unauthorized request.
     *
     * @returns {boolean} True if the error status code is 401 Unauthorized, false otherwise.
     * @public
     */
    isUnauthorized() {
      return this[STATUS_CODE_PROPERTY] === ERROR_CODE.UNAUTHORIZED;
    }

    /**
     * Checks if the provided error represents an unauthorized request.
     *
     * @param {Error} error - The error to inspect.
     * @returns {boolean} True if the error status code is 401 Unauthorized, false otherwise.
     * @public
     * @static
     */
    static isUnauthorized(error) {
      return error && error[STATUS_CODE_PROPERTY] === ERROR_CODE.UNAUTHORIZED;
    }

    /**
     * Checks if the HTTP error status code indicates a forbidden request.
     *
     * @returns {boolean} True if the error status code is 403 Forbidden, false otherwise.
     * @public
     */
    isForbidden() {
      return this[STATUS_CODE_PROPERTY] === ERROR_CODE.FORBIDDEN;
    }

    /**
     * Checks if the provided error represents a forbidden request.
     *
     * @param {Error} error - The error to inspect.
     * @returns {boolean} True if the error status code is 403 Forbidden, false otherwise.
     * @public
     * @static
     */
    static isForbidden(error) {
      return error && error[STATUS_CODE_PROPERTY] === ERROR_CODE.FORBIDDEN;
    }

    /**
     * Checks if the HTTP error status code indicates a "Not Found" response.
     *
     * @returns {boolean} True if the error status code is 404 Not Found, false otherwise.
     * @public
     */
    isFileNotFound() {
      return this[STATUS_CODE_PROPERTY] === ERROR_CODE.NOT_FOUND;
    }

    /**
     * Checks if the given error is an instance of HttpError or has the characteristics of one.
     *
     * @param {Error} error - The error to check.
     * @returns {boolean} True if the error is an HttpError or has a 'statusCode' property, false otherwise.
     * @public
     * @static
     */
    static isHttpError(error) {
      return error && !!error[STATUS_CODE_PROPERTY];
    }

    /**
     * Checks if the provided error represents a "file not found" condition.
     * This is meant to be used for checking error duck typing HttpError, so it checks
     * the statusCode property.
     *
     * @param {Error} error - The error to inspect.
     * @returns {boolean} True if the error indicates a "file not found" condition, false otherwise.
     * @public
     * @static
     */
    static isFileNotFound(error) {
      return error && error[STATUS_CODE_PROPERTY] === ERROR_CODE.NOT_FOUND;
    }
  }

  return HttpError;
});

